// src/theme/united.js — the panel's one theme file (MGMT-PANEL-BUILD-SHELL-01).
//
// Direction A, "Calm operations" (PD2 directions/a-gtm.html:12-13), light by default.
// Where A matches PD1's contrast fixes (REDESIGN_PROPOSAL.md §1) the fixed value is used:
// light muted rgba(0,0,0,0.62), light accent-as-text #B0334B, light danger #C62828,
// light info #4A6FA5, dark accent-as-text #E0607A. `faint` is PD1's (ThemeContext.tsx:15/91)
// and is for placeholders and disabled text only (it fails body-text contrast by design).
//
// Everything that colours the panel reads from here: the CSS variables set on <html> by
// PanelThemeProvider, the Material UI theme, index.css and genesisData.js THEME.
import { createTheme } from '@mui/material/styles';

export const SCHEMES = ['light', 'dark'];
export const DEFAULT_SCHEME = 'light';
export const STORAGE_KEY = 'united.panel.scheme';

export const FONT_SANS = "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif";
export const FONT_DISPLAY = "'Space Grotesk', 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
export const FONT_MONO = "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

export const tokens = {
    light: {
        bg: '#F5F0EB',
        side: '#EFE9E2',
        s1: '#FFFFFF',
        s2: '#FAF6F1',
        s3: '#EFE8E0',
        hover: 'rgba(60,40,20,0.05)',
        line: 'rgba(60,40,20,0.10)',
        line2: 'rgba(60,40,20,0.17)',
        ink: '#1E1914',
        muted: 'rgba(0,0,0,0.62)',
        faint: 'rgba(0,0,0,0.3)',
        accent: '#C63D57',
        accentHover: '#B5354E',
        accentText: '#B0334B',
        accentTint: 'rgba(198,61,87,0.10)',
        accentFaint: 'rgba(198,61,87,0.06)',
        onAccent: '#FFFFFF',
        ok: '#2E7D32',
        warn: '#B45309',
        warnTint: 'rgba(180,83,9,0.11)',
        danger: '#C62828',
        info: '#4A6FA5',
        infoTint: 'rgba(74,111,165,0.11)',
        scrim: 'rgba(30,20,10,0.42)',
        top: 'rgba(245,240,235,0.86)',
        grid: 'rgba(60,40,20,0.08)',
    },
    dark: {
        bg: '#0F0E0D',
        side: '#121110',
        s1: '#181614',
        s2: '#201D1A',
        s3: '#2A2622',
        hover: 'rgba(255,255,255,0.045)',
        line: 'rgba(255,255,255,0.07)',
        line2: 'rgba(255,255,255,0.12)',
        ink: '#F4EFE9',
        muted: '#A59D94',
        faint: 'rgba(255,255,255,0.3)',
        accent: '#C63D57',
        accentHover: '#B5354E',
        accentText: '#E0607A',
        accentTint: 'rgba(198,61,87,0.16)',
        accentFaint: 'rgba(198,61,87,0.08)',
        onAccent: '#FFFFFF',
        ok: '#4CAF50',
        warn: '#F59E0B',
        warnTint: 'rgba(245,158,11,0.15)',
        danger: '#ef4444',
        info: '#7eb6fb',
        infoTint: 'rgba(126,182,251,0.15)',
        scrim: 'rgba(5,4,3,0.66)',
        top: 'rgba(15,14,13,0.82)',
        grid: 'rgba(255,255,255,0.06)',
    },
};

// token name -> CSS variable name
const VAR_NAMES = {
    bg: '--bg', side: '--side', s1: '--s1', s2: '--s2', s3: '--s3', hover: '--hover',
    line: '--line', line2: '--line2', ink: '--ink', muted: '--muted', faint: '--faint',
    accent: '--accent', accentHover: '--accent-hover', accentText: '--accent-text',
    accentTint: '--accent-tint', accentFaint: '--accent-faint', onAccent: '--on-accent',
    ok: '--ok', warn: '--warn', warnTint: '--warn-tint', danger: '--danger', info: '--info',
    infoTint: '--info-tint', scrim: '--scrim', top: '--top', grid: '--grid',
};

// `v.accent` -> 'var(--accent)', for inline styles that must follow the scheme.
export const v = Object.fromEntries(Object.entries(VAR_NAMES).map(([k, name]) => [k, `var(${name})`]));

export const normalizeScheme = (s) => (SCHEMES.includes(s) ? s : DEFAULT_SCHEME);

export function cssVarsFor(scheme) {
    const t = tokens[normalizeScheme(scheme)];
    const out = {};
    Object.entries(VAR_NAMES).forEach(([k, name]) => { out[name] = t[k]; });
    return out;
}

export function muiThemeFor(scheme) {
    const s = normalizeScheme(scheme);
    const t = tokens[s];
    // Material UI computes hover and contrast shades from these, so they are real colours, not var().
    return createTheme({
        palette: {
            mode: s,
            background: { default: t.bg, paper: t.s1 },
            primary: { main: t.accent, contrastText: t.onAccent },
            secondary: { main: t.accentText },
            error: { main: t.danger },
            warning: { main: t.warn },
            success: { main: t.ok },
            info: { main: t.info },
            text: { primary: t.ink, secondary: t.muted, disabled: t.faint },
            divider: t.line,
            action: { hover: t.hover },
        },
        typography: { fontFamily: FONT_SANS },
        shape: { borderRadius: 10 },
    });
}
